import { useParams, Link } from "react-router-dom";
import { useMemo } from "react";
import { Phone, ArrowLeft, ArrowRight, CheckCircle2, MessageSquareText, Layers } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { Reveal } from "@/components/common/Reveal";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { business, services } from "@/data/site";
import NotFound from "@/pages/NotFound";

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = services.find((s) => s.slug === slug);
  const requestState = useMemo(() => ({ service: service?.title }), [service]);
  if (!service) return <NotFound />;

  const Icon = service.icon;
  const others = services.filter((s) => s.slug !== slug);
  const isContain = service.fit === "contain";

  return (
    <div>
      <PageHeader eyebrow="Service" title={service.title} description={service.short}>
        <div className="flex flex-wrap gap-3">
          <Button asChild variant="brand" size="lg">
            <a href={business.phoneHref}><Phone className="h-4 w-4" /> Call {business.phone}</a>
          </Button>
          <Button asChild variant="glass" size="lg">
            <Link to="/services"><ArrowLeft className="h-4 w-4" /> All services</Link>
          </Button>
        </div>
      </PageHeader>

      {/* ===== OVERVIEW ===== */}
      <section className="section-py">
        <div className="mx-auto grid max-w-7xl gap-10 container-px lg:grid-cols-2">
          <Reveal>
            <div className={`relative overflow-hidden rounded-3xl border border-border shadow-card ${isContain ? "bg-muted" : ""}`}>
              <img
                src={service.image}
                alt={service.title}
                className={`aspect-[4/3] w-full ${isContain ? "object-contain p-6" : "object-cover"}`}
              />
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="flex h-full flex-col">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary text-primary-foreground shadow-yellow">
                <Icon className="h-7 w-7" />
              </div>
              <h2 className="mt-5 text-2xl font-bold text-foreground sm:text-3xl">
                What we do with {service.title.toLowerCase()}
              </h2>
              <ul className="mt-6 space-y-3">
                {service.details.map((d) => (
                  <li key={d} className="flex items-start gap-3 text-[15px] text-foreground/90">
                    <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-accent" /> {d}
                  </li>
                ))}
              </ul>

              {service.products && (
                <div className="mt-7 rounded-2xl border border-border bg-muted/40 p-6">
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                    Brands we service
                  </p>
                  <div className="mt-4 flex flex-wrap gap-2.5">
                    {[...new Set(service.products.map((p) => p.brand))].map((b) => (
                      <span key={b} className="rounded-lg border border-border bg-card px-3.5 py-1.5 text-sm font-semibold text-foreground">
                        {b}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <Button asChild variant="brand" size="lg">
                  <Link to="/contact" state={requestState}>
                    Request this service <ArrowRight className="h-4 w-4" />
                  </Link>
                </Button>
                <Button asChild variant="outlineDark" size="lg">
                  <a href={business.smsHref}><MessageSquareText className="h-4 w-4" /> Text a question</a>
                </Button>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ===== GALLERY ===== */}
      {service.gallery && (
        <section className="pb-4">
          <div className="mx-auto max-w-7xl container-px">
            <h2 className="text-2xl font-bold text-foreground sm:text-3xl">
              A range of {service.title.toLowerCase()} we work with
            </h2>
            <p className="mt-2 text-[15px] text-muted-foreground">
              A few examples of the variety we deliver, set up and repair.
            </p>
            <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {service.gallery.map((src, i) => (
                <Reveal key={src} delay={(i % 3) * 0.06}>
                  <div className="overflow-hidden rounded-2xl border border-border bg-muted shadow-soft">
                    <img
                      src={src}
                      alt={`${service.title} example ${i + 1}`}
                      loading="lazy"
                      className="aspect-[4/3] w-full object-cover transition-transform duration-500 hover:scale-105"
                    />
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ===== OTHER SERVICES ===== */}
      <section className="bg-muted section-py">
        <div className="mx-auto max-w-7xl container-px">
          <div className="flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.16em] text-muted-foreground">
            <Layers className="h-4 w-4 text-primary" /> Explore other services
          </div>
          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {others.map((o) => {
              const OIcon = o.icon;
              return (
                <Card key={o.slug} className="card-hover border-border bg-card p-0">
                  <Link to={`/services/${o.slug}`} className="flex h-full items-center gap-3 p-5">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-secondary text-primary">
                      <OIcon className="h-5 w-5" />
                    </div>
                    <span className="text-[15px] font-semibold text-foreground">{o.title}</span>
                    <ArrowRight className="ml-auto h-4 w-4 shrink-0 text-muted-foreground" />
                  </Link>
                </Card>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}
